// @ts-check
/* eslint-env browser */

import { reactiveStore } from '../reactive-store.js'

/** @typedef {import('@geoman-io/maplibre-geoman-free').ModeName} ModeName */

/**
 * Creates an event interceptor that prevents editing unselected features.
 * It reads the selected feature ID from the reactive store on each event.
 *
 * @param {import('@geoman-io/maplibre-geoman-free').Geoman} geoman - The Geoman instance
 * @returns {(event: any) => { next: boolean }}
 */
function preventEditingUnselectedFeatures (geoman) {
  return (event) => {
    try {
      // Get current selected feature ID from reactive store
      const currentSelectedId = reactiveStore.selectedFeatureId.get()
      if (!currentSelectedId) {
        // No feature selected, block all edits
        return { next: false }
      }

      // Get the Geoman ID for the currently selected feature
      const currentSelectedGeomanId = getGeomanFeatureByPropertyId(geoman, currentSelectedId).id
      if (currentSelectedGeomanId === undefined) {
        // Selected feature not found, block edits
        return { next: false }
      }

      const featureData = geoman.features.getFeatureByMouseEvent({
        event,
        sourceNames: ['gm_main']
      })

      // If no feature data, allow the event (might be clicking on map)
      if (!featureData) {
        return { next: true }
      }

      // Get the actual feature being interacted with
      let actualFeatureId = featureData.id

      // If this is a marker (vertex/edge), get the parent feature ID
      if (featureData.parent) {
        actualFeatureId = featureData.parent.id
      }

      // Only allow edits on the selected feature
      if (actualFeatureId !== currentSelectedGeomanId) {
        // Block the edit - this is not the selected feature
        return { next: false }
      }

      // Allow the edit - this is the selected feature
      return { next: true }
    } catch (error) {
      console.debug('Edit interceptor error:', error)
      return { next: true }
    }
  }
}

/**
 * Determines the intended draw shape for a feature with null/empty geometry
 * @param {string} id - The feature _id property
 * @returns {'polygon'|'marker'} - The draw shape to use
 */
function getIntendedDrawShape (id) {
  const features = reactiveStore.features.get()
  const feature = features.find(f => f.properties._id === id)
  if (!feature) return 'polygon'
  return feature.properties?.feature_type === 'tree' ? 'marker' : 'polygon'
}

/**
 * Gets the Geoman ID for a feature by its _id property
 * @param {import('@geoman-io/maplibre-geoman-free').Geoman} geoman - The Geoman instance
 * @param {string} id - The feature _id property
 * @returns {any | undefined} The Geoman feature entry, or undefined if not found
 */
export function getGeomanFeatureByPropertyId (geoman, id) {
  let feature
  geoman.features.featureStore.forEach((entry) => {
    try {
      const gj = entry.getGeoJson()
      if (gj?.properties?._id === id) {
        feature = entry
      }
    } catch {
      // ignore invalid features
    }
  })
  return feature
}

/**
 * Sets the edit mode on a specific feature or disables edit mode if no feature is selected
 * @param {import('@geoman-io/maplibre-geoman-free').Geoman} geoman - The Geoman instance
 * @param {import('maplibre-gl').Map} map - The MapLibre map instance
 * @param {string} id - The feature ID to edit, or empty string to disable edit mode
 * @param {ModeName|'add'|'add_point'|'magic_wand'} editMode - The edit mode to enable, "add" to allow adding features, "add_point" to place a point marker, or "magic_wand" for area selection
 */
export function editFeature (geoman, map, id, editMode) {
  if (!geoman || !reactiveStore.mapReady.get()) {
    return
  }

  // Remove existing point selection indicator
  if (map.getLayer('selected-point-indicator')) {
    map.removeLayer('selected-point-indicator')
    map.removeSource('selected-point-indicator')
  }

  // If ID is empty, disable edit mode (interceptor will block all edits)
  if (!id || id === 'main') {
    geoman.disableAllModes()
    return
  }

  // Find the Geoman ID for the selected feature
  const selectedGeomanFeature = getGeomanFeatureByPropertyId(geoman, id)
  if (!selectedGeomanFeature) {
    // Feature not in Geoman (null geometry)
    if (editMode === 'add') {
      const shape = getIntendedDrawShape(id)
      geoman.disableAllModes()
      geoman.enableMode('draw', shape)
      // Re-enable snapping if it was enabled
      if (reactiveStore.snap.get()) {
        geoman.enableMode('helper', 'snapping')
      }
      return
    } else if (editMode === 'add_point') {
      geoman.disableAllModes()
      geoman.enableMode('draw', 'marker')
      // Re-enable snapping if it was enabled
      if (reactiveStore.snap.get()) {
        geoman.enableMode('helper', 'snapping')
      }
      return
    } else if (editMode === 'magic_wand') {
      // Magic wand handles null geometry features directly
      return
    } else {
      // For other modes, disable edit mode
      geoman.disableAllModes()
      return
    }
  }
  const selectedGeomanId = selectedGeomanFeature.id
  if (selectedGeomanId === undefined) {
    return
  }

  // Temporarily remove unselected features, enable edit mode (so handles appear only on selected feature),
  // then re-add unselected features (they won't get handles since edit mode was enabled before adding them)

  // Step 1: Collect unselected features and verify selected feature exists
  const unselectedFeatures = []
  let selectedFeatureExists = false
  geoman.features.featureStore.forEach((entry) => {
    try {
      const shape = entry.shape
      // Filter out edit markers (vertex_marker, edge_marker, center_marker)
      if (shape !== 'vertex_marker' && shape !== 'edge_marker' && shape !== 'center_marker') {
        if (entry.id === selectedGeomanId) {
          selectedFeatureExists = true
        } else {
          const gj = entry.getGeoJson()
          unselectedFeatures.push({ geoJson: gj, id: entry.id })
        }
      }
    } catch {
      // ignore
    }
  })

  if (!selectedFeatureExists) {
    return
  }

  // Step 2: Disable edit mode
  geoman.disableAllModes()

  // Step 3: Remove unselected features temporarily
  // Check if features still exist before deleting (disableAllModes may have modified featureStore)
  const unselectedIds = new Set(unselectedFeatures.map(f => f.id))

  geoman.features.featureStore.forEach((entry) => {
    if (unselectedIds.has(entry.id)) {
      try {
        geoman.features.delete(entry.id)
      } catch {
        // ignore
      }
    }
  })

  // Step 4: Enable edit mode (handles will appear only on the selected feature, which is still on the map)
  if (editMode === 'add') {
    const shape = getIntendedDrawShape(id)
    geoman.enableMode('draw', shape)
    // Don't register interceptor for "add" mode - allow drawing new features
  } else if (editMode === 'add_point') {
    geoman.enableMode('draw', 'marker')
    // Don't register interceptor for "add_point" mode - allow placing marker
  } else if (editMode === 'magic_wand') {
    // Don't enable Geoman edit mode - magic-wand handles clicks directly
    // Don't register interceptor - allow clicking anywhere on map
  } else {
    geoman.enableMode('edit', editMode)

    // Geoman 0.6.x no longer auto-enables shape markers in drag mode
    // We want it for all modes except cut, where the shape_markers breaks the functionality
    if (editMode === 'cut') {
      geoman.disableMode('helper', 'shape_markers')
    } else {
      geoman.enableMode('helper', 'shape_markers')
    }

    // Register interceptor (to prevent editing unselected features) - events.bus only fires when edit mode is active
    const interceptor = preventEditingUnselectedFeatures(geoman)
    geoman.events.bus.on('mousedown', interceptor)
    geoman.events.bus.on('touchstart', interceptor)
  }

  // Re-enable snapping if it was enabled (disableAllModes() disables it)
  if (reactiveStore.snap.get()) {
    geoman.enableMode('helper', 'snapping')
  }

  // For overlays, hide midpoint handles in "change" mode
  const removeMidpointHandles = () => {
    if (editMode !== 'change' || !selectedGeomanFeature.getGeoJson()?.properties?._isMapOverlay) return
    geoman.features.featureStore.forEach((entry) => {
      if (entry.shape === 'edge_marker') {
        try { geoman.features.delete(entry.id) } catch {}
      }
    })
  }

  // Step 5: Re-add unselected features (they won't get edit handles since edit mode was enabled before adding them)
  requestAnimationFrame(() => {
    removeMidpointHandles()

    // Get current feature IDs to check for duplicates
    const existingIds = new Set()
    geoman.features.forEach((feature) => {
      existingIds.add(feature.id)
    })

    unselectedFeatures.forEach((feature) => {
      try {
        // Only import if not already present
        if (!existingIds.has(feature.id)) {
          geoman.features.importGeoJson(feature.geoJson)
        }
      } catch (error) {
        console.error('[editFeature] Error re-importing feature:', error, feature.id)
      }
    })

    // Add selection indicator for points
    const selectedEntry = geoman.features.featureStore.get(selectedGeomanId)
    if (selectedEntry) {
      try {
        const gj = selectedEntry.getGeoJson()
        if (gj?.geometry?.type === 'Point') {
          const isSatellite = reactiveStore.tileType.get() === 'satellite'
          const strokeColor = isSatellite ? '#FF0000' : '#3388ff'
          const sourceId = 'selected-point-indicator'
          map.addSource(sourceId, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })
          /** @type {import('maplibre-gl').GeoJSONSource | undefined} */
          const source = map.getSource(sourceId)
          source?.setData({ type: 'Feature', geometry: gj.geometry, properties: {} })
          if (!map.getLayer(sourceId)) {
            map.addLayer({
              id: sourceId,
              type: 'circle',
              source: sourceId,
              paint: {
                'circle-radius': 15,
                'circle-color': 'transparent',
                'circle-stroke-color': strokeColor,
                'circle-stroke-width': 3
              }
            })
          }
        }
      } catch {
        // ignore
      }
    }
    // Run once more after re-adding features to catch any late-created edge markers.
    removeMidpointHandles()
  })
}
